import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { TabSwitcher, FilterChip } from "@/components/shared/PagePrimitives";
import { DepositDialog, IssueDialog } from "@/components/cashbox/CashDialogs";
import { CaseDetailPanel } from "@/components/cashbox/CaseDetailPanel";
import { formatCZK, formatDate } from "@/lib/format";
import { Banknote, Users, BookOpen, PiggyBank, HandCoins, ReceiptText, CheckCircle2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ask } from "@/lib/confirm";

const CHIP_CLS = {
  vklad: "bg-success/15 text-success",
  vydano: "bg-secondary text-muted-foreground",
  doklady: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  kontrola: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  vyporadani: "bg-brand/15 text-brand",
  uzavreno: "bg-secondary text-muted-foreground",
};
const Chip = ({ chip }) => (
  <span className={`rounded-full px-2.5 py-0.5 text-[12px] font-bold whitespace-nowrap ${CHIP_CLS[chip?.key] || "bg-secondary text-muted-foreground"}`}>
    {chip?.label}
  </span>
);

export default function EconomicsCashbox() {
  const { user } = useAuth();
  const admin = ["OWNER", "ASSISTANT"].includes(user?.role);
  const [d, setD] = useState(null);
  const [view, setView] = useState("people");
  const [book, setBook] = useState(null);
  const [filter, setFilter] = useState("vse");
  const [dlg, setDlg] = useState(null);            // "deposit" | "zaloha" | "vydaj"
  const [caseId, setCaseId] = useState(null);
  const [autoSettle, setAutoSettle] = useState(false);
  const [editMov, setEditMov] = useState(null);   // úprava vkladu (zadání 81/A)

  const load = useCallback(() => api.get("/cashbox").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))), []);
  const loadBook = useCallback((f) => api.get(`/cashbox/book?filter=${f}`).then((r) => setBook(r.data.rows)).catch(() => {}), []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (view === "book") loadBook(filter); }, [view, filter, loadBook]);

  const reload = () => { load(); if (view === "book") loadBook(filter); };

  // Předání/schválení jde rovnou z karty; vypořádání a nahrání účtenky přes panel.
  const runAction = async (a) => {
    if (!a) return;
    if (a.key === "to_review" || a.key === "approve") {
      try {
        await api.post(`/cashbox/cases/${a.case_id}/advance`, { to: a.key === "to_review" ? "kontrola" : "vyporadani" });
        toast.success(a.key === "to_review" ? "Předáno asistentce." : "Vyúčtování schváleno.");
        reload();
      } catch (e) { toast.error(apiError(e)); }
      return;
    }
    setAutoSettle(a.key.startsWith("settle"));
    setCaseId(a.case_id);
  };

  const openCase = (id) => { setAutoSettle(false); setCaseId(id); };

  const deleteDeposit = async (m) => {
    if (!await ask(`Smazat vklad ${formatCZK(m.amount)}? Zůstatek se sníží.`)) return;
    try { await api.delete(`/cashbox/movements/${m.id}`); toast.success("Vklad smazán."); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!d) return <div className="text-muted-foreground py-8 text-center" data-testid="cashbox-loading">Načítám pokladnu…</div>;

  return (
    <div className="space-y-5" data-testid="cashbox-page">
      <p className="text-muted-foreground text-sm -mt-1" data-testid="cashbox-subtitle">
        Firemní hotovost{d.location ? ` · ${d.location}` : ""} · {d.open_count} otevřených případů
      </p>

      {/* Karta zůstatku — plné částky (formatCZK), přetečení řeší velikost písma. */}
      <div className="glass rounded-[20px] p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid="balance-card">
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-1">V pokladně</div>
          <div className={`font-heading font-extrabold tracking-tight tabular-nums whitespace-nowrap text-2xl sm:text-3xl ${d.balance < 0 ? "text-destructive" : "text-brand"}`}
            data-testid="balance">
            {formatCZK(d.balance)}
          </div>
          {d.balance < 0 && (
            <div className="text-[13px] font-semibold text-destructive mt-0.5" data-testid="balance-warning">
              Pokladna je v minusu — zkontrolujte zaevidované vklady.
            </div>
          )}
          <div className="text-[13px] text-muted-foreground mt-1">
            V terénu u lidí: <b className="text-foreground tabular-nums">{formatCZK(d.held_total)}</b>
            {d.last_movement_date && <> · poslední pohyb {formatDate(d.last_movement_date)}</>}
          </div>
        </div>
        {admin && (
          <div className="md:ml-auto flex flex-col md:flex-row gap-2 shrink-0">
            <Button variant="outline" onClick={() => setDlg("deposit")} className="h-11 w-full md:w-auto font-semibold" data-testid="deposit-btn">
              <PiggyBank size={16} /> Vklad
            </Button>
            <Button variant="outline" onClick={() => setDlg("vydaj")} className="h-11 w-full md:w-auto font-semibold" data-testid="expense-btn">
              <ReceiptText size={16} /> Přímý výdaj
            </Button>
            <Button onClick={() => setDlg("zaloha")} className="h-11 w-full md:w-auto font-bold" data-testid="advance-btn">
              <HandCoins size={16} /> Vydat zálohu
            </Button>
          </div>
        )}
      </div>

      <TabSwitcher value={view} onChange={setView} testid="cashbox-tabs"
        tabs={[{ value: "people", label: "Lidé", icon: Users, testid: "tab-people" },
               { value: "book", label: "Pokladní kniha", icon: BookOpen, testid: "tab-book" }]} />

      {view === "people" && (
        <>
          {d.people.length === 0 ? (
            <div className="bg-card border border-border rounded-[20px] p-8 text-center" data-testid="people-empty">
              <Banknote size={40} className="mx-auto text-muted-foreground mb-3" />
              <p className="font-bold">Nikdo teď nedrží firemní hotovost.</p>
              <p className="text-muted-foreground text-sm mt-1">Vydejte zálohu nebo přímý výdaj — případ se objeví tady.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="people-grid">
              {d.people.map((p, i) => {
                const overdue = p.oldest_days > 14;
                return (
                  <div key={p.person_id} data-testid={`person-card-${i}`}
                    className={`bg-card border rounded-[20px] p-4 flex flex-col ${overdue ? "border-destructive/50" : "border-border"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-bold truncate">{p.person_name}</div>
                        <div className="text-[12px] text-muted-foreground">
                          {p.open_count} {p.open_count === 1 ? "případ" : p.open_count < 5 ? "případy" : "případů"} · nejstarší {p.oldest_days} d
                        </div>
                      </div>
                      {p.waiting_on_us && (
                        <span className="rounded-full px-2.5 py-0.5 text-[11px] font-bold bg-brand/15 text-brand whitespace-nowrap">čeká na nás</span>
                      )}
                    </div>
                    <div className={`text-2xl sm:text-3xl font-heading font-extrabold tracking-tight tabular-nums whitespace-nowrap mt-1 ${
                      p.saldo > 0 ? "text-brand" : p.saldo < 0 ? "text-destructive" : "text-foreground"}`}
                      data-testid={`person-saldo-${i}`}>
                      {formatCZK(p.saldo)}
                    </div>
                    <div className="space-y-1.5 mt-2 mb-3">
                      {p.cases.map((c) => (
                        <button key={c.id} type="button" onClick={() => openCase(c.id)} data-testid={`case-row-${c.human_id}`}
                          className="w-full flex items-center gap-2 text-left rounded-[10px] px-2 py-1.5 -mx-2 hover:bg-secondary transition-colors">
                          <span className="text-[12px] font-bold text-muted-foreground tabular-nums shrink-0">{c.human_id}</span>
                          <span className="text-[13px] truncate flex-1 min-w-0">{c.purpose}</span>
                          <Chip chip={{ key: c.status, label: c.status === "vydano" ? "Vydáno" : c.status === "doklady" ? "Doklady" : c.status === "kontrola" ? "Kontrola" : "K vypořádání" }} />
                        </button>
                      ))}
                    </div>
                    {admin && p.action && (
                      <Button onClick={() => runAction(p.action)} data-testid={`person-action-${i}`}
                        variant={p.action.variant === "primary" ? "default" : "outline"}
                        className="h-11 w-full font-bold mt-auto">
                        {p.action.label}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {d.settled.length > 0 && (
            <div className="bg-card border border-border rounded-[20px] p-4" data-testid="settled-list">
              <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Vyrovnáno</div>
              <div className="flex flex-wrap gap-2">
                {d.settled.map((p) => (
                  <span key={p.person_id} className="inline-flex items-center gap-1.5 bg-secondary rounded-full px-3 py-1.5 text-sm font-semibold text-muted-foreground">
                    <CheckCircle2 size={14} className="text-success" /> {p.person_name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {view === "book" && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {[["vse", "Vše"], ["vklady", "Vklady"], ["vydaje", "Výdaje"], ["otevrene", "Otevřené"]].map(([v, l]) => (
              <FilterChip key={v} label={l} active={filter === v} onClick={() => setFilter(v)} testid={`filter-${v}`} />
            ))}
          </div>
          {!book ? (
            <p className="text-muted-foreground py-6 text-center">Načítám knihu…</p>
          ) : book.length === 0 ? (
            <div className="bg-card border border-border rounded-[20px] p-8 text-center" data-testid="book-empty">
              <BookOpen size={36} className="mx-auto text-muted-foreground mb-3" />
              <p className="font-bold">Žádné pohyby.</p>
              <p className="text-muted-foreground text-sm mt-1">Pro tento filtr v knize nic není.</p>
            </div>
          ) : (
            <>
              {/* Desktop tabulka — roluje jen uvnitř karty */}
              <div className="hidden md:block bg-card border border-border rounded-[20px] p-2" data-testid="book-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                        <th className="px-3 py-2.5">Datum</th>
                        <th className="px-3 py-2.5">Pohyb</th>
                        <th className="px-3 py-2.5">Stav</th>
                        <th className="px-3 py-2.5 text-right">Částka</th>
                        {admin && <th className="px-3 py-2.5 w-0"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {book.map((m) => (
                        <tr key={m.id} data-testid={`book-row-${m.id}`}
                          onClick={() => m.case_id && openCase(m.case_id)}
                          className={`border-t border-border/50 ${m.case_id ? "cursor-pointer hover:bg-secondary/50" : ""}`}>
                          <td className="px-3 py-2.5 whitespace-nowrap tabular-nums">{formatDate(m.date)}</td>
                          <td className="px-3 py-2.5 min-w-0">
                            <span className="font-semibold">{m.person_name}</span>
                            {m.human_id && <span className="text-muted-foreground"> · {m.human_id}</span>}
                            {m.note && <span className="text-muted-foreground"> · {m.note}</span>}
                          </td>
                          <td className="px-3 py-2.5"><Chip chip={m.chip} /></td>
                          <td className={`px-3 py-2.5 text-right font-bold tabular-nums whitespace-nowrap ${m.signed_amount > 0 ? "text-success" : "text-foreground"}`}>
                            {m.signed_amount > 0 ? "+" : "−"}{formatCZK(Math.abs(m.signed_amount))}
                          </td>
                          {admin && (
                            <td className="px-2 py-2.5 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                              {m.kind === "vklad" && (
                                <div className="flex items-center justify-end gap-1">
                                  <button onClick={() => setEditMov(m)} data-testid={`deposit-edit-${m.id}`} title="Upravit vklad"
                                    className="p-1.5 rounded-md text-muted-foreground hover:text-brand hover:bg-secondary transition-colors"><Pencil size={15} /></button>
                                  <button onClick={() => deleteDeposit(m)} data-testid={`deposit-delete-${m.id}`} title="Smazat vklad"
                                    className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-secondary transition-colors"><Trash2 size={15} /></button>
                                </div>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Mobil karty */}
              <div className="md:hidden space-y-2" data-testid="book-cards">
                {book.map((m) => {
                  const inner = (
                    <>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">
                          {m.person_name}{m.human_id ? ` · ${m.human_id}` : ""}
                        </div>
                        <div className="text-[12px] text-muted-foreground truncate">{formatDate(m.date)}{m.note ? ` · ${m.note}` : ""}</div>
                        <div className="mt-1"><Chip chip={m.chip} /></div>
                      </div>
                      <div className={`font-bold tabular-nums whitespace-nowrap ${m.signed_amount > 0 ? "text-success" : ""}`}>
                        {m.signed_amount > 0 ? "+" : "−"}{formatCZK(Math.abs(m.signed_amount))}
                      </div>
                    </>
                  );
                  if (admin && m.kind === "vklad") {
                    return (
                      <div key={m.id} className="bg-card border border-border rounded-[16px] p-3.5 flex items-center gap-3">
                        {inner}
                        <div className="flex flex-col gap-1 shrink-0">
                          <button onClick={() => setEditMov(m)} data-testid={`deposit-edit-m-${m.id}`} title="Upravit vklad"
                            className="p-2 rounded-md text-muted-foreground hover:text-brand hover:bg-secondary transition-colors"><Pencil size={16} /></button>
                          <button onClick={() => deleteDeposit(m)} data-testid={`deposit-delete-m-${m.id}`} title="Smazat vklad"
                            className="p-2 rounded-md text-muted-foreground hover:text-destructive hover:bg-secondary transition-colors"><Trash2 size={16} /></button>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <button key={m.id} type="button" onClick={() => m.case_id && openCase(m.case_id)}
                      className="w-full text-left bg-card border border-border rounded-[16px] p-3.5 flex items-center gap-3">
                      {inner}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      <DepositDialog open={dlg === "deposit"} onClose={() => setDlg(null)}
        onDone={() => { setDlg(null); reload(); }} />
      <DepositDialog open={!!editMov} movement={editMov} onClose={() => setEditMov(null)}
        onDone={() => { setEditMov(null); reload(); }} />
      <IssueDialog kind={dlg === "vydaj" ? "vydaj" : "zaloha"} open={dlg === "zaloha" || dlg === "vydaj"}
        onClose={() => setDlg(null)}
        onDone={(c) => { setDlg(null); reload(); openCase(c.id); }} />
      <CaseDetailPanel caseId={caseId} open={!!caseId} admin={admin} autoSettle={autoSettle}
        onClose={() => { setCaseId(null); setAutoSettle(false); }} onChanged={reload} />
    </div>
  );
}
